import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createSipEndpoint, deleteSipEndpoint, fetchSipEndpoints, patchSipEndpoint } from "../../../admin/api";
import { adminStyles as styles } from "../shared/adminStyles";
import ActionIconButton from "../shared/ActionIconButton";
import AdminModalPortal from "../shared/AdminModalPortal";
import { useAdminNotifications } from "../notifications/AdminNotificationsContext";
import { normalizeSearchValue } from "../utils/adminFormatters";
import { useAdminTextSearch } from "../utils/useAdminTextSearch";
import "./SipEndpointsDirectory.css";

const EMPTY_FORM = { name: "", sip_number: "", transfer_number: "", description: "" };
const COLUMN_WIDTHS = [22, 31, 13, 24, 10];

function SipEndpointForm({ item, onClose, onSaved }) {
  const [draft, setDraft] = useState(() => item ? {
    name: item.name || "",
    sip_number: item.sip_number || "",
    transfer_number: String(item.transfer_number ?? ""),
    description: item.description || "",
  } : EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const change = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: "" }));
  };
  const submit = async (event) => {
    event.preventDefault();
    const transferNumber = Number(draft.transfer_number);
    const errors = {};
    if (!draft.name.trim()) errors.name = "Введите название";
    if (!draft.sip_number.trim()) errors.sip_number = "Введите SIP";
    if (!String(draft.transfer_number).trim()) errors.transfer_number = "Введите номер перевода";
    else if (!Number.isInteger(transferNumber)) errors.transfer_number = "Номер перевода должен быть целым числом";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;
    try {
      setIsSaving(true);
      setSubmitError("");
      const payload = {
        name: draft.name.trim(),
        sip_number: draft.sip_number.trim(),
        transfer_number: transferNumber,
        description: draft.description.trim(),
      };
      await (item?._id ? patchSipEndpoint(item._id, payload) : createSipEndpoint(payload));
      await onSaved(Boolean(item?._id));
      onClose();
    } catch (saveError) {
      setSubmitError("Не удалось сохранить SIP. Проверьте поля и повторите попытку.");
    } finally {
      setIsSaving(false);
    }
  };

  return <AdminModalPortal isOpen onClose={onClose} closeDisabled={isSaving} dialogClassName="sip-endpoint-modal" ariaLabel={item?._id ? "Редактировать SIP" : "Добавить SIP"}>
    <form onSubmit={submit} className="sip-endpoint-form">
      <header className="sip-endpoint-form__header">
        <h2 style={styles.title}>{item?._id ? "Редактировать SIP" : "Добавить SIP"}</h2>
        <ActionIconButton icon="close" label="Закрыть" disabled={isSaving} onClick={onClose} />
      </header>
      <div className="sip-endpoint-form__body">
        {submitError ? <p style={styles.filterError}>{submitError}</p> : null}
        <div className="sip-endpoint-form__grid">
          <label className="sip-endpoint-form__field sip-endpoint-form__field--full"><span>Название *</span><input placeholder="Яндекс Магазин лидов" style={styles.input} value={draft.name} onChange={(e) => change("name", e.target.value)} />{fieldErrors.name ? <small>{fieldErrors.name}</small> : null}</label>
          <label className="sip-endpoint-form__field"><span>SIP *</span><input placeholder="72001001348@domain.voximplant.com" style={styles.input} value={draft.sip_number} onChange={(e) => change("sip_number", e.target.value)} />{fieldErrors.sip_number ? <small>{fieldErrors.sip_number}</small> : null}</label>
          <label className="sip-endpoint-form__field sip-endpoint-form__field--number"><span>Номер перевода *</span><input type="number" step="1" placeholder="496" style={styles.input} value={draft.transfer_number} onChange={(e) => change("transfer_number", e.target.value)} />{fieldErrors.transfer_number ? <small>{fieldErrors.transfer_number}</small> : null}</label>
          <label className="sip-endpoint-form__field sip-endpoint-form__field--full"><span>Описание</span><textarea placeholder="Описание" value={draft.description} onChange={(e) => change("description", e.target.value)} /></label>
        </div>
      </div>
      <footer className="sip-endpoint-form__footer">
        <button type="button" style={styles.viewButton} disabled={isSaving} onClick={onClose}>Отмена</button>
        <button type="submit" style={styles.button} disabled={isSaving}>{isSaving ? "Сохранение…" : item?._id ? "Сохранить" : "Добавить"}</button>
      </footer>
    </form>
  </AdminModalPortal>;
}

export default function SipEndpointsDirectory({ active }) {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingItem, setEditingItem] = useState(undefined);
  const loadedRef = useRef(false);
  const requestRef = useRef(0);
  const { notify } = useAdminNotifications();
  const debouncedQuery = useAdminTextSearch(query);

  const refresh = useCallback(async () => {
    const requestId = ++requestRef.current;
    try {
      setIsLoading(true);
      setError("");
      const response = await fetchSipEndpoints();
      if (requestId !== requestRef.current) return;
      setItems(Array.isArray(response?.items) ? response.items : []);
      loadedRef.current = true;
    } catch (loadError) {
      if (requestId === requestRef.current) setError(loadError instanceof Error ? loadError.message : "Не удалось загрузить справочник SIP");
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (active && !loadedRef.current) refresh(); }, [active, refresh]);

  const filteredItems = useMemo(() => {
    const needle = normalizeSearchValue(debouncedQuery.value);
    if (!needle) return items;
    return items.filter((item) => [item.name, item.sip_number, item.transfer_number, item.description]
      .map(normalizeSearchValue).join(" ").includes(needle));
  }, [debouncedQuery.value, items]);

  const saved = async (wasEditing) => {
    await refresh();
    notify(wasEditing ? "SIP обновлён" : "SIP добавлен", "success");
  };
  const remove = async (item) => {
    if (!window.confirm(`Удалить SIP «${item.name}»?`)) return;
    try {
      await deleteSipEndpoint(item._id);
      await refresh();
      notify("SIP удалён", "success");
    } catch (deleteError) {
      notify(deleteError instanceof Error ? deleteError.message : "Не удалось удалить SIP", "error");
    }
  };
  const copy = async (item) => {
    await navigator.clipboard.writeText(item.sip_number);
    notify("SIP скопирован", "success");
  };

  return <section style={{ ...styles.documentsCard, display: active ? "flex" : "none" }}>
    <div style={styles.documentsHeader}>
      <div><h2 style={styles.documentsTitle}>Справочник SIP</h2><span style={styles.muted}>Записей: {filteredItems.length} / {items.length}</span></div>
      <button type="button" style={styles.button} onClick={() => setEditingItem(null)}>+ Добавить SIP</button>
    </div>
    <div style={{ ...styles.controls, marginTop: "var(--admin-gap)" }}>
      <input style={styles.input} placeholder="Поиск по названию, SIP, номеру или описанию..." value={query} onChange={(event) => setQuery(event.target.value)} />
    </div>
    <div style={styles.documentsScroll}>
      {error ? <div style={styles.state}>{error}</div> : isLoading && !loadedRef.current ? <div style={styles.state}>Загрузка SIP...</div> : filteredItems.length === 0 ? <div style={styles.state}>В справочнике пока нет SIP</div> : <div style={styles.tableWrap}>
        <table style={{ ...styles.table, tableLayout: "fixed" }}>
          <colgroup>{COLUMN_WIDTHS.map((width) => <col key={width} style={{ width: `${width}%` }} />)}</colgroup>
          <thead><tr>{["Название", "SIP", "Номер перевода", "Описание", "Действия"].map((label) => <th key={label} style={styles.th}>{label}</th>)}</tr></thead>
          <tbody>{filteredItems.map((item) => <tr key={item._id}>
            <td style={styles.td}>{item.name}</td><td style={{ ...styles.td, overflowWrap: "anywhere" }} title={item.sip_number}>{item.sip_number}</td>
            <td style={styles.td}>{item.transfer_number}</td><td style={styles.td} title={item.description || ""}><div className="sip-endpoint-description">{item.description || "—"}</div></td>
            <td style={{ ...styles.td, ...styles.actionsTd }}><div className="admin-row-actions">
              <ActionIconButton icon="edit" label="Редактировать SIP" onClick={() => setEditingItem(item)} />
              <ActionIconButton icon="copy" label="Скопировать SIP" onClick={() => copy(item)} />
              <ActionIconButton icon="trash" tone="danger" label="Удалить SIP" onClick={() => remove(item)} />
            </div></td>
          </tr>)}</tbody>
        </table>
      </div>}
    </div>
    {typeof editingItem !== "undefined" ? <SipEndpointForm item={editingItem} onClose={() => setEditingItem(undefined)} onSaved={saved} /> : null}
  </section>;
}
